import { useClarifyingAnswers } from '@/lib/proposals/hooks/useClarifyingAnswers';

import type { ProposalDetail } from '../../../lib/proposals/core/types';
import { ClarifyingQuestionsPanel } from './ClarifyingQuestionsPanel';

/**
 * Owns the clarifying-answers state (autosave, submit, answered/submitted
 * tracking) for a proposal and renders the ClarifyingQuestionsPanel card.
 *
 * Rendered by ProposalDetailPanel into ProposalContent's `clarifyingPanel`
 * slot — the card lives in the CONTENT region under the Action Overview,
 * never in the footer (product decision 2026-09-01, task 4). The answers
 * autosave registers with the AutoSaveCoordinationProvider wrapping the
 * detail panel, so pressing approve flushes pending answer saves exactly
 * like any other editor (task 3).
 */
export function ProposalClarifyingQuestions({
  proposal,
  editable,
  onSubmitAnswers,
}: {
  proposal: ProposalDetail;
  /** False while the proposal is not pending or a chat turn is running —
   * answers render read-only. */
  editable: boolean;
  /** Called after answers are successfully submitted (kicks off the
   * refine-with-answers reasoning turn). */
  onSubmitAnswers?: () => void;
}) {
  const clarifying = useClarifyingAnswers({
    orgId: proposal.org,
    proposalId: proposal.id,
    clarifyingQuestions: proposal.clarifying_questions,
    editable,
    onSubmitAnswers,
  });

  const questions = proposal.clarifying_questions?.questions ?? [];
  if (questions.length === 0) {
    return null;
  }

  return (
    // Keyed by question_set_id so a new question set remounts the card and
    // re-evaluates its expanded-by-default state.
    <ClarifyingQuestionsPanel
      key={proposal.clarifying_questions?.question_set_id ?? 'question-set'}
      questions={questions}
      answers={clarifying.answers}
      saveState={clarifying.saveState}
      isSubmitting={clarifying.isSubmitting}
      disabled={!editable || clarifying.isSubmitting}
      onSelectOption={clarifying.selectOption}
      onToggleOption={clarifying.toggleOption}
      onSetOther={clarifying.setOther}
      onSubmit={clarifying.submitAnswers}
      hasSubmitted={clarifying.hasSubmitted}
      orgId={proposal.org}
    />
  );
}
